import type { z } from 'zod'
import { subscribeInvalidation } from '../../services/dashboard-invalidation'

import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  interactionAppointmentContextSchema,
  interactionInsuranceContextSchema,
  interactionPatientContextSchema,
  interactionSummarySchema,
} from './conversations-contracts'

export type InteractionPatientContext = z.infer<
  typeof interactionPatientContextSchema
>
export type InteractionAppointmentContext = z.infer<
  typeof interactionAppointmentContextSchema
>
export type InteractionInsuranceContext = z.infer<
  typeof interactionInsuranceContextSchema
>
export type InteractionSummary = z.infer<typeof interactionSummarySchema>

export type InteractionContextRepository = {
  subscribe?: (tenantId: string, listener: () => void) => () => void
  getPatientContext: (
    tenantId: string,
    aggregateId: string
  ) => Promise<InteractionPatientContext>
  getAppointmentContext: (
    tenantId: string,
    aggregateId: string
  ) => Promise<InteractionAppointmentContext>
  getInsuranceContext?: (
    tenantId: string,
    aggregateId: string
  ) => Promise<InteractionInsuranceContext>
  getSummary?: (
    tenantId: string,
    aggregateId: string
  ) => Promise<InteractionSummary>
}

export function createInteractionContextRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): InteractionContextRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  const path = (tenantId: string, aggregateId: string, resource: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates/${encodeURIComponent(aggregateId)}/${resource}?contextVersion=2`
  return {
    // Context and summary follow the conversation and its Action Requests.
    subscribe: (tenantId, listener) =>
      subscribeInvalidation(tenantId, listener, [
        'conversations',
        'aggregates',
        'operational',
      ]),
    getPatientContext(tenantId, aggregateId) {
      return client.request(path(tenantId, aggregateId, 'patient-context'), {
        method: 'GET',
        cache: 'no-store',
        responseSchema: interactionPatientContextSchema,
      })
    },
    getAppointmentContext(tenantId, aggregateId) {
      return client.request(
        path(tenantId, aggregateId, 'appointment-context'),
        {
          method: 'GET',
          cache: 'no-store',
          responseSchema: interactionAppointmentContextSchema,
        }
      )
    },
    getInsuranceContext(tenantId, aggregateId) {
      return client.request(path(tenantId, aggregateId, 'insurance-context'), {
        method: 'GET',
        cache: 'no-store',
        responseSchema: interactionInsuranceContextSchema,
      })
    },
    getSummary(tenantId, aggregateId) {
      const summary = `${path(tenantId, aggregateId, 'summary')}&interactionFacts=1`
      return client.request(summary, {
        method: 'GET',
        cache: 'no-store',
        responseSchema: interactionSummarySchema,
      })
    },
  }
}
